import React, { useState, forwardRef, useImperativeHandle, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next'; //语言包
import { connect } from 'react-redux';
import { InfoCircleOutlined } from '@ant-design/icons';
import { Form, Input, Divider, Typography, Button, Space, Row, Col, Radio } from 'antd';
import { VerifyFormWrapper, VeriryResultWrapper, LoggerSNVeriryResultWrapper, PSIDVeriryResultWrapper, ResultInfoWrapper, LoggerModeWrapper } from './style';
import { VerifyOperation } from '../../../operation';
import { WithUnit, WithRequiredMark } from '../../../textOrnament';
import moment from 'moment';
import service from '../../../../request';
import { attachHalfWidthAutoConvert } from '../../../../utils/charUtils';

const PRIMARY_ATTACH_DESC_DEFAULT = '192.168.1.15:552';
const STANDBY_ATTACH_DESC_DEFAULT = '192.168.1.35:552';

const PmuForm = forwardRef((props, ref) => {
    const { Text } = Typography;
    const { t } = useTranslation();
    const [form] = Form.useForm();
    const [serialIdValid, setSerialIdValid] = useState(false);
    const [showAdditionalInfo, setShowAdditionalInfo] = useState(false);
    const [result, setResult] = useState({
        addType: '', //验证通过后的类型
        idOrSn: '', //电站ID或采集器序列号
        attachDesc: '', //附加信息
        mode: 1, //集群模式 0-单机模式
    });
    const [validating, setValidating] = useState(false);
    const [textInfo, setTextInfo] = useState({
        paramsOne: '',
        paramsTwo: '',
        paramsThree: '',
    });
    const isErrState = useRef(0);
    const ipPortInputRef = useRef(null);
    useEffect(() => {
        // 监听原生 input + compositionend，处理 IME 全角字符自动转半角
        return attachHalfWidthAutoConvert(ipPortInputRef.current?.input);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [showAdditionalInfo, result.addType]);

    /**
     * 获取当前步骤的默认附加信息。
     * @description 首次添加采集器时返回主采集器默认值，第二次添加时返回备用采集器默认值，用于统一输入框显示值和提交值。
     * @returns {string} 当前步骤对应的默认附加信息。
     * @throws {void} 该方法不抛出异常。
     */
    const getDefaultAttachDesc = () => {
        return props.mode ? PRIMARY_ATTACH_DESC_DEFAULT : STANDBY_ATTACH_DESC_DEFAULT;
    };
    // 验证序列号/ID
    /**
     * 校验采集器序列号或电站 ID。
     * @description 当校验结果为采集器时，会把默认附加信息同步写入表单和值状态，避免页面展示了默认值但父组件提交时拿到空值。
     * @returns {Promise<void>} 异步校验完成后结束，无显式返回值。
     * @throws {Error} 当接口调用失败或校验不通过时，通过表单错误信息反馈异常情况。
     */
    const validateSerialId = async () => {
        try {
            const value = form.getFieldValue('serialId');
            if (value === undefined || value === null || value.trim() === '') {
                form.setFields([
                    {
                        name: 'serialId',
                        errors: [t('All.Result.Error.FormRules.requiredField')],
                    },
                ]);
                return;
            }
            var tempResult = {
                addType: '', //验证通过后的类型
                idOrSn: value, //电站ID或采集器序列号
                attachDesc: '', //附加信息
                mode: 1,
            };

            //重复验证
            if (props.present?.idOrSn !== undefined && props.present?.idOrSn !== null) {
                // 检查value是否包含在props.present数组中
                if (props.present?.idOrSn === value && value !== '') {
                    setSerialIdValid(false);
                    form.setFields([
                        {
                            name: 'serialId',
                            errors: [t('All.Result.Error.FormRules.usedSN')],
                        },
                    ]);
                    return;
                }
            }

            setValidating(true);
            // 这里可以添加API调用验证序列号是否存在
            const res = await service.get('service-psinfo/pmu/isExistPmuOrPs?ps_id_sn=' + value);
            isErrState.current = 0;
            var isValid = false;
            var msg = '';
            if (!props.mode && res.data?.type === 'psId') {
                res.status = 655;
            }
            switch (res.status) {
                case 626:
                    msg = t('All.Result.Error.FormRules.noRepeatPlantRoleRelationship');
                    break;
                case 646:
                    msg = t('All.Result.Error.FormRules.noSuchLoggerSerialNumber');
                    break;
                case 655:
                    msg = t('All.Result.Error.invalidSN');
                    break;
                case 656:
                    msg = t('All.Result.Error.FormRules.usedSN');
                    break;
                case 657:
                    msg = t('All.Result.Error.FormRules.invalidLoggerSerialNumberOrPlantID');
                    break;
                case 200:
                    //验证通过，检查是否为采集器序列号，如果是则需要检查是否在初始化中
                    if (res.data.type !== undefined && res.data.type === 'pmuId') {
                        // 检查采集器是否正在初始化中
                        try {
                            const initCheckRes = await service.get('service-psinfo/pmusn/checkPmuInitializing?pmuSn=' + value);
                            if (initCheckRes.success && initCheckRes.data === true) {
                                // 正在初始化中，设置错误并直接返回
                                setSerialIdValid(false);
                                setShowAdditionalInfo(false);
                                form.setFields([
                                    {
                                        name: 'serialId',
                                        errors: [t('All.Result.Error.FormRules.uninitializedLogger'/** 采集器正在初始化中 */)],
                                    },
                                ]);
                                return;
                            }
                        } catch (initError) {
                            console.error('检查初始化状态失败:', initError);
                        }
                    }
                    
                    isValid = true;
                    tempResult.addType = res.data.type;
                    if (res.data.type === 'pmuId') {
                        const defaultAttachDesc = getDefaultAttachDesc();
                        tempResult.attachDesc = defaultAttachDesc;
                        // 这里在校验成功后立即回填默认值，避免父组件提交时仍旧读取到空字符串。
                        form.setFieldsValue({ ipPort: defaultAttachDesc });
                    } else {
                        form.setFieldsValue({ ipPort: undefined });
                    }
                    setResult(tempResult);
                    isErrState.current = res.status;
                    if (res.data.type !== undefined && res.data.type === 'pmuId') {
                        setTextInfo({
                            paramsOne: res.data.modelName,
                            paramsTwo: res.data.brandName,
                            paramsThree: res.data.payNum,
                        });
                    } else if (res.data.type !== undefined && res.data.type === 'psId') {
                        setTextInfo({
                            paramsOne: res.data.psName,
                            paramsTwo: res.data.psTypeName,
                            paramsThree: moment(res.data.psBuilddate).format('YYYY-MM-DD'),
                        });
                    }
                    break;
                default:
                    break;
            }

            if (res.status !== 200) {
                form.setFields([
                    {
                        name: 'serialId',
                        errors: [msg],
                    },
                ]);
            } else {
                form.setFields([
                    {
                        name: 'serialId',
                        errors: [],
                    },
                ]);
            }
            // 设置验证结果
            setSerialIdValid(isValid);
            setShowAdditionalInfo(isValid);
        } catch (error) {
            setSerialIdValid(false);
            setShowAdditionalInfo(false);
            const errorMsg = t('验证失败，请重试');
            form.setFields([
                {
                    name: 'serialId',
                    errors: [errorMsg],
                },
            ]);
        } finally {
            setValidating(false);
        }
    };

    // 验证附加信息
    /**
     * 校验附加信息输入的 IP 与端口格式。
     * @description 校验通过后会同步更新结果状态，保证父组件步骤跳转和最终提交都读取到最新附加信息。
     * @param {object} _ 校验规则对象，占位参数，无实际业务含义。
     * @param {string} value 当前输入的附加信息值。
     * @returns {Promise<void>} 校验通过时返回成功 Promise。
     * @throws {Error} 当附加信息为空、格式错误、IP 非法或端口越界时抛出校验错误。
     */
    const validateIpPort = (_, value) => {
        var temp = { ...result };
        temp.attachDesc = '';
        setResult(temp);
        if (!value && showAdditionalInfo) {
            return Promise.reject(new Error(t('All.Note.Placeholder.enterIPAdress&PortNumber')));
        }

        if (value) {

            //重复验证
            if (props.present?.attachDesc !== undefined && props.present?.attachDesc !== null) {
                // 检查value是否包含在props.present数组中
                if (props.present?.attachDesc === value && value !== '') {
                    return Promise.reject(new Error(t('All.Result.Error.FormRules.usedAdditionalInfo')));
                }
            }

            // 验证IP地址和端口格式 (IP:PORT)
            const ipPortRegex = /^(\d{1,3}\.){3}\d{1,3}:\d{1,5}$/;
            if (!ipPortRegex.test(value)) {
                return Promise.reject(new Error(t('All.Result.Error.FormRules.incorrectIPPortNumberFormat')));
            }

            // 验证IP地址合法性
            const ipPart = value.split(':')[0];
            const ipSegments = ipPart.split('.');
            for (let segment of ipSegments) {
                const num = parseInt(segment);
                if (num < 0 || num > 255) {
                    return Promise.reject(new Error(t('All.Result.Error.FormRules.invalidIPRule')));
                }
            }

            // 验证端口合法性
            const portPart = value.split(':')[1];
            const port = parseInt(portPart);
            if (port < 1 || port > 65535) {
                return Promise.reject(new Error(t('All.Result.Error.FormRules.invalidPNRule')));
            }
        }
        temp.attachDesc = value;
        setResult(temp);
        return Promise.resolve();
    };

    // 暴露给父组件的方法
    /**
     * 校验当前表单并返回可直接用于提交的结果对象。
     * @description 这里显式把表单中的附加信息合并到返回值中，避免依赖 React 异步状态同步导致提交值丢失。
     * @returns {Promise<object>} 返回包含 addType、idOrSn、attachDesc、mode 的完整结果。
     * @throws {Error} 当表单任一字段校验失败时抛出异常。
     */
    const handleValidateForm = async () => {
        const values = await form.validateFields();
        const attachDesc = result.addType === 'pmuId'
            ? (values.ipPort || result.attachDesc || getDefaultAttachDesc())
            : '';

        return {
            ...result,
            attachDesc,
        };
    };

    useImperativeHandle(ref, () => ({
        getFormValues: () => form.getFieldsValue(),
        validateForm: handleValidateForm,
        resetForm: () => {
            form.resetFields();
            setSerialIdValid(false);
            setShowAdditionalInfo(false);
            setResult({
                addType: '', //验证通过后的类型
                idOrSn: '', //电站ID或采集器序列号
                attachDesc: '', //附加信息
                mode: 1, //集群模式 0-单机模式
            });
        },
        serialIdValid,
        validateSerialId,
    }));

    //电站id 或采集器 序列号验证后调用事件
    useEffect(() => {
        console.log('验证结果:', result);
        props.onValidate(result);
    }, [result]);

    return (
        <>
            <Form className='AddForm' form={form} layout='vertical' autoComplete='off'>
                <VerifyFormWrapper>
                    <Divider className='DividerWarning' orientation='left' orientationMargin='0'>
                        <WithRequiredMark carrierContent={props.mode ? t('All.Parameter.loggerSerialNumberOrPlantID') : t('All.Parameter.loggerSN')} />
                    </Divider>
                    <Form.Item>
                        <Input.Group compact size='large'>
                            <Form.Item name='serialId' noStyle hasFeedback >
                                <Input
                                    placeholder={props.mode ? t('All.Note.Placeholder.enterLoggerSerialNumberOrPlantID') : t('All.Note.Placeholder.enterLoggerSerialNumber')}
                                    onChange={(e) => {
                                        setSerialIdValid(false);
                                        setShowAdditionalInfo(false);
                                        setResult({
                                            addType: '', //验证通过后的类型
                                            idOrSn: '', //电站ID或采集器序列号
                                            attachDesc: '', //附加信息
                                            mode: 1, //集群模式 0-单机模式
                                        });
                                    }}
                                />
                            </Form.Item>
                            <VerifyOperation onClick={validateSerialId} disabled={showAdditionalInfo} loading={validating} />
                        </Input.Group>
                    </Form.Item>
                </VerifyFormWrapper>

                {showAdditionalInfo && result.addType === 'pmuId' && (
                    <>
                        <Divider className='DividerWarning' orientation='left' orientationMargin='0'>
                            <WithRequiredMark carrierContent={t('All.Parameter.additionalInfo')} />
                        </Divider>
                        <Form.Item
                            name='ipPort'
                            initialValue={getDefaultAttachDesc()}
                            rules={[
                                {
                                    required: true,
                                    validator: validateIpPort,
                                },
                            ]}
                            validateTrigger={['onBlur']}
                            hasFeedback>
                            <Input
                                ref={ipPortInputRef}
                                placeholder={t('All.Note.Placeholder.enterIPAdress&PortNumber')}
                            />
                        </Form.Item>
                    </>
                )}
            </Form>

            <VeriryResultWrapper className={`checked ${isErrState.current === 200 ? 'show DividerCenter' : 'isHide'}}`}>
                <PSIDVeriryResultWrapper className={result.addType === 'psId' ? 'show' : 'isHide'}>
                    <ResultInfoWrapper>
                        <Space>
                            <Typography.Text type='secondary'>{t('All.Parameter.psName')}</Typography.Text>
                            <Typography.Text>{textInfo.paramsOne}</Typography.Text>
                        </Space>
                        <Space>
                            <Typography.Text type='secondary'>{t('All.Parameter.psType')}</Typography.Text>
                            <Typography.Text>{textInfo.paramsTwo}</Typography.Text>
                        </Space>
                        <Space>
                            <Typography.Text type='secondary'>{t('All.Parameter.psGridConnectionDate')}</Typography.Text>
                            <Typography.Text>{textInfo.paramsThree}</Typography.Text>
                        </Space>
                    </ResultInfoWrapper>
                </PSIDVeriryResultWrapper>
                <LoggerSNVeriryResultWrapper className={result.addType === 'pmuId' ? 'Show' : 'isHide'}>
                    <ResultInfoWrapper>
                        <Row gutter={[16, 16]}>
                            <Col span={12}>
                                <Space>
                                    <Typography.Text type='secondary'> {t('All.Parameter.loggerModal')}</Typography.Text>
                                    <Typography.Text>{textInfo.paramsOne}</Typography.Text>
                                </Space>
                            </Col>
                            <Col span={12}>
                                <Space>
                                    <Typography.Text type='secondary'>{t('All.Parameter.loggerBrand')}</Typography.Text>
                                    <Typography.Text>{textInfo.paramsTwo}</Typography.Text>
                                </Space>
                            </Col>
                            <Col span={12}>
                                <Space>
                                    <Typography.Text type='secondary'>{t('All.Parameter.bindLimit')}</Typography.Text>
                                    <Typography.Text>{textInfo.paramsThree}</Typography.Text>
                                </Space>
                            </Col>
                            <Col span={12}>
                                <Space>
                                    <Typography.Text type='secondary'>{t('All.Parameter.servicePeriod')}</Typography.Text>
                                    <Typography.Text>{moment().format('YYYY-MM-DD') + t('All.Parameter.serviceThrough') + moment().add(2, 'years').format('YYYY-MM-DD')}</Typography.Text>
                                </Space>
                            </Col>
                        </Row>
                    </ResultInfoWrapper>
                    <Divider />
                    <LoggerModeWrapper className={props.mode ? 'show' : 'isHide'}>
                        <Text>{t('All.Team.LoggerMode.loggerMode')}</Text>
                        <Radio.Group value={result.mode} onChange={e => setResult({ ...result, mode: e.target.value })}>
                            <Radio value={1}>{t('All.Team.LoggerMode.clusterMode')}</Radio>
                            <Radio value={2}>{t('All.Team.LoggerMode.standaloneMode')}</Radio>
                        </Radio.Group>
                    </LoggerModeWrapper>
                </LoggerSNVeriryResultWrapper>
            </VeriryResultWrapper>
        </>
    );
});

const mapStateToProps = state => {
    return {
        language: state.getIn(['header', 'global', 'language']),
    };
};

const mapDippathToProps = dispatch => {
    return {
        handleLoad() { },
    };
};

export default connect(mapStateToProps, mapDippathToProps, null, { forwardRef: true })(PmuForm);
